/* Clothxtra collection grid */
if (!customElements.get('cx-collection')) {
  class CxCollection extends HTMLElement {
    connectedCallback() {
      this.drawer = this.querySelector('[data-cx-fs]');
      this.form = this.querySelector('[data-cx-fs-form]');
      this.grid = this.querySelector('[data-cx-grid]');
      this.qv = this.querySelector('[data-cx-qv]');
      this.qvBody = this.querySelector('[data-cx-qv-body]');
      try {
        this.strings = JSON.parse(this.querySelector('[data-cx-qv-strings]')?.textContent || '{}');
      } catch (e) {
        this.strings = {};
      }

      /* ---- Filter drawer ---- */
      this.querySelector('[data-cx-fs-open]')?.addEventListener('click', () => this.openDialog(this.drawer));
      this.querySelector('[data-cx-fs-close]')?.addEventListener('click', () => this.closeDialog(this.drawer));
      this.drawer?.addEventListener('click', (e) => {
        if (e.target === this.drawer) this.closeDialog(this.drawer);
      });

      // submit without empty params (e.g. blank price boxes)
      this.form?.addEventListener('submit', (e) => {
        e.preventDefault();
        const qs = this.formQuery().toString();
        window.location.href = `${this.dataset.url}${qs ? `?${qs}` : ''}`;
      });

      /* ---- Chip rows: ‹ › arrows ---- */
      this.querySelectorAll('[data-cx-row-group]').forEach((group) => {
        const row = group.querySelector('[data-cx-row]');
        const arrows = group.querySelector('.cx-fs__arrows');
        if (!row || !arrows) return;
        const prev = arrows.querySelector('[data-cx-row-prev]');
        const next = arrows.querySelector('[data-cx-row-next]');
        const sync = () => {
          const max = row.scrollWidth - row.clientWidth;
          arrows.hidden = max <= 2;
          prev.disabled = row.scrollLeft <= 2;
          next.disabled = row.scrollLeft >= max - 2;
        };
        prev.addEventListener('click', () => row.scrollBy({ left: -row.clientWidth * 0.8 }));
        next.addEventListener('click', () => row.scrollBy({ left: row.clientWidth * 0.8 }));
        row.addEventListener('scroll', sync, { passive: true });
        group._cxSync = sync;
      });

      /* ---- Live "Show N results" ---- */
      this.applyBtn = this.querySelector('[data-cx-fs-apply]');
      this.form?.addEventListener('change', () => {
        clearTimeout(this.countTimer);
        this.countTimer = setTimeout(() => this.updateCount(), 300);
      });

      /* ---- Size dropdown: close on outside click / Esc ---- */
      this.dropdowns = Array.from(this.querySelectorAll('[data-cx-dd]'));
      document.addEventListener('click', (e) => {
        this.dropdowns.forEach((dd) => {
          if (dd.open && !dd.contains(e.target)) dd.open = false;
        });
      });
      document.addEventListener('keyup', (e) => {
        if (e.key === 'Escape') this.dropdowns.forEach((dd) => (dd.open = false));
      });

      /* ---- Quick add / quick view ---- */
      this.addEventListener('click', (e) => {
        const open = e.target.closest('[data-cx-qv-open]');
        if (open) {
          e.preventDefault();
          this.openQuickView(open);
          return;
        }
        const add = e.target.closest('[data-cx-add]');
        if (add) {
          e.preventDefault();
          this.addToCart(add.dataset.cxAdd, add);
        }
      });
      this.querySelector('[data-cx-qv-close]')?.addEventListener('click', () => this.closeDialog(this.qv));
      this.qv?.addEventListener('click', (e) => {
        if (e.target === this.qv) this.closeDialog(this.qv);
      });

      /* ---- Load more / infinite scroll ---- */
      this.setupMore();
    }

    /* ================= Dialog helpers ================= */
    openDialog(dialog) {
      if (!dialog) return;
      dialog.showModal();
      document.documentElement.classList.add('cx-scroll-lock');
      dialog.addEventListener('close', () => document.documentElement.classList.remove('cx-scroll-lock'), { once: true });
      if (dialog === this.drawer) {
        requestAnimationFrame(() => this.querySelectorAll('[data-cx-row-group]').forEach((g) => g._cxSync?.()));
      }
    }

    closeDialog(dialog) {
      if (!dialog?.open || dialog.classList.contains('is-closing')) return;
      const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      dialog.classList.add('is-closing');
      setTimeout(
        () => {
          dialog.classList.remove('is-closing');
          dialog.close();
        },
        reduce ? 0 : 200
      );
    }

    /* ================= Filters ================= */
    formQuery() {
      const params = new URLSearchParams();
      new FormData(this.form).forEach((value, key) => {
        if (String(value).trim() !== '') params.append(key, value);
      });
      return params;
    }

    async updateCount() {
      if (!this.applyBtn) return;
      this.countAbort?.abort();
      this.countAbort = new AbortController();
      const params = this.formQuery();
      params.set('section_id', this.dataset.section);
      this.applyBtn.classList.add('is-loading');
      try {
        const res = await fetch(`${this.dataset.url}?${params.toString()}`, { signal: this.countAbort.signal });
        const html = await res.text();
        const el = new DOMParser().parseFromString(html, 'text/html').querySelector('cx-collection[data-total]');
        if (el) {
          this.applyBtn.textContent = (this.applyBtn.dataset.label || 'Show [count] results').replace('[count]', el.dataset.total);
        }
      } catch (e) {
        if (e.name !== 'AbortError') console.error('cx-filter-count', e);
      } finally {
        this.applyBtn.classList.remove('is-loading');
      }
    }

    /* ================= Load more ================= */
    setupMore() {
      this.more = this.querySelector('[data-cx-more]');
      this.querySelector('[data-cx-top]')?.addEventListener('click', () => {
        const top = this.getBoundingClientRect().top + window.scrollY - 120;
        window.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });
      });
      if (!this.more || !this.grid) return;

      this.more.addEventListener('click', (e) => {
        if (!e.target.closest('[data-cx-more-btn]')) return;
        e.preventDefault();
        this.loadMore();
      });

      if (this.more.dataset.mode === 'infinite' && this.more.dataset.next && 'IntersectionObserver' in window) {
        this.io = new IntersectionObserver(
          (entries) => {
            if (entries.some((en) => en.isIntersecting)) this.loadMore();
          },
          { rootMargin: '0px 0px 600px 0px' }
        );
        this.io.observe(this.more);
      }
    }

    async loadMore() {
      const next = this.more?.dataset.next;
      if (!next || this.loadingMore) return;
      this.loadingMore = true;
      const btn = this.more.querySelector('[data-cx-more-btn]');
      const spinner = this.more.querySelector('[data-cx-more-spinner]');
      btn?.classList.add('is-loading');
      if (spinner) spinner.hidden = false;

      try {
        const url = new URL(next, window.location.origin);
        url.searchParams.set('section_id', this.dataset.section);
        const res = await fetch(url.toString());
        const doc = new DOMParser().parseFromString(await res.text(), 'text/html');

        doc.querySelectorAll('[data-cx-grid] > li').forEach((li) => this.grid.appendChild(document.importNode(li, true)));

        const newMore = doc.querySelector('[data-cx-more]');
        const newCount = newMore?.querySelector('[data-cx-more-count]');
        const count = this.more.querySelector('[data-cx-more-count]');
        if (newCount && count) count.textContent = newCount.textContent;

        const nextUrl = newMore?.dataset.next || '';
        this.more.dataset.next = nextUrl;
        if (!nextUrl) {
          btn?.remove();
          this.io?.disconnect();
        } else if (btn) {
          btn.href = nextUrl;
        }
      } catch (err) {
        console.error('cx-load-more', err);
      } finally {
        this.loadingMore = false;
        btn?.classList.remove('is-loading');
        if (spinner) spinner.hidden = true;
        // keep loading if the sentinel is still on screen
        if (this.io && this.more.dataset.next) {
          const r = this.more.getBoundingClientRect();
          if (r.top < window.innerHeight + 600) requestAnimationFrame(() => this.loadMore());
        }
      }
    }

    /* ================= Money ================= */
    money(cents) {
      const format = this.dataset.moneyFormat || '{{amount}}';
      const n = Number(cents || 0) / 100;
      const fmt = (num, dec, thou = ',', dsep = '.') => {
        const [i, d] = num.toFixed(dec).split('.');
        return i.replace(/\B(?=(\d{3})+(?!\d))/g, thou) + (d ? dsep + d : '');
      };
      return format.replace(/\{\{\s*(\w+)\s*\}\}/, (_, key) => {
        switch (key) {
          case 'amount_no_decimals':
            return fmt(n, 0);
          case 'amount_with_comma_separator':
            return fmt(n, 2, '.', ',');
          case 'amount_no_decimals_with_comma_separator':
            return fmt(n, 0, '.');
          case 'amount_with_apostrophe_separator':
            return fmt(n, 2, "'");
          case 'amount_with_space_separator':
            return fmt(n, 2, ' ', ',');
          default:
            return fmt(n, 2);
        }
      });
    }

    priceHTML(price, compare) {
      let html = `<span class="cx-pc__now">${this.money(price)}</span>`;
      if (compare && compare > price) {
        html += `<s class="cx-pc__was">${this.money(compare)}${this.escape(this.dataset.asterisk || '')}</s>`;
      }
      return html;
    }

    escape(str) {
      const d = document.createElement('div');
      d.textContent = str == null ? '' : String(str);
      return d.innerHTML;
    }

    imgUrl(src, width) {
      if (!src) return '';
      const s = typeof src === 'string' ? src : src.src || '';
      if (!s) return '';
      return `${s}${s.includes('?') ? '&' : '?'}width=${width}`;
    }

    /* ================= Quick view ================= */
    async openQuickView(trigger) {
      if (!this.qv) return;
      this.qvTrigger = trigger;
      this.qvBody.innerHTML = '<div class="cx-qv__loading"><span></span></div>';
      this.openDialog(this.qv);
      this.qv.addEventListener('close', () => this.qvTrigger?.focus({ preventScroll: true }), { once: true });

      try {
        const root = window.Shopify?.routes?.root || '/';
        const res = await fetch(`${root}products/${trigger.dataset.cxQvOpen}.js`);
        if (!res.ok) throw new Error(res.status);
        const product = await res.json();
        product._url = trigger.dataset.url || product.url;
        this.renderQuickView(product);
      } catch (err) {
        console.error('cx-quick-view', err);
        window.location.href = trigger.dataset.url;
      }
    }

    renderQuickView(product) {
      const options = (product.options || []).map((o, i) =>
        typeof o === 'string'
          ? { name: o, values: [...new Set(product.variants.map((v) => v.options[i]))] }
          : { name: o.name, values: o.values }
      );
      // single-value options are picked automatically, the rest wait for the shopper
      const selected = options.map((o) => (o.values.length === 1 ? o.values[0] : null));
      this.qvState = { product, options, selected };

      const optsHTML = options
        .map((opt, i) => {
          if (opt.values.length === 1) {
            return '';
          }
          const values = opt.values
            .map(
              (val) =>
                `<button type="button" class="cx-qv__val" data-opt="${i}" data-val="${this.escape(val)}" aria-pressed="false">${this.escape(val)}</button>`
            )
            .join('');
          return `<div class="cx-qv__opt" data-opt-wrap="${i}">
              <p class="cx-qv__label">${this.escape(opt.name)}: <span data-opt-selected="${i}"></span></p>
              <div class="cx-qv__values" role="group" aria-label="${this.escape(opt.name)}">${values}</div>
            </div>`;
        })
        .join('');

      const fixed = options
        .filter((o) => o.values.length === 1 && !(o.name === 'Title' && o.values[0] === 'Default Title'))
        .map((o) => `<p class="cx-qv__meta">${this.escape(o.name)}: ${this.escape(o.values[0])}</p>`)
        .join('');

      const img = this.imgUrl(product.featured_image, 400);
      this.qvBody.innerHTML = `
        <div class="cx-qv__top">
          <div class="cx-qv__img">${img ? `<img src="${img}" alt="${this.escape(product.title)}" data-qv-img>` : ''}</div>
          <div>
            <h2 class="cx-qv__title"><a href="${this.escape(product._url)}">${this.escape(product.title)}</a></h2>
            ${fixed}
            <a class="cx-qv__link" href="${this.escape(product._url)}">${this.escape(this.strings.details || 'View full details')}</a>
          </div>
        </div>
        ${optsHTML}
        <div class="cx-qv__buy">
          <div class="cx-qv__price" data-qv-price></div>
          <button type="button" class="cx-qv__add" data-qv-add>${this.escape(this.strings.add || 'Add To Cart')}</button>
        </div>
        <p class="cx-qv__msg" data-qv-msg role="alert"></p>`;

      this.qvBody.querySelectorAll('.cx-qv__val').forEach((btn) =>
        btn.addEventListener('click', () => {
          const i = Number(btn.dataset.opt);
          this.qvState.selected[i] = btn.dataset.val;
          this.qvBody.querySelector(`[data-opt-wrap="${i}"]`)?.classList.remove('is-missing');
          this.qvBody.querySelector('[data-qv-msg]').textContent = '';
          this.updateQuickView();
        })
      );
      this.qvBody.querySelector('[data-qv-add]').addEventListener('click', () => this.quickViewAdd());
      this.updateQuickView();
    }

    currentVariant() {
      const { product, selected } = this.qvState;
      if (selected.some((v) => v === null)) return null;
      return product.variants.find((v) => v.options.every((o, i) => o === selected[i])) || null;
    }

    updateQuickView() {
      const { product, options, selected } = this.qvState;
      const variant = this.currentVariant();

      // buttons: pressed + unavailable state
      options.forEach((opt, i) => {
        this.qvBody.querySelectorAll(`.cx-qv__val[data-opt="${i}"]`).forEach((btn) => {
          const val = btn.dataset.val;
          btn.setAttribute('aria-pressed', String(selected[i] === val));
          const ok = product.variants.some(
            (v) => v.available && v.options[i] === val && v.options.every((o, k) => k === i || selected[k] === null || o === selected[k])
          );
          btn.classList.toggle('is-unavailable', !ok);
        });
        const label = this.qvBody.querySelector(`[data-opt-selected="${i}"]`);
        if (label) label.textContent = selected[i] || '';
      });

      // price
      const priceEl = this.qvBody.querySelector('[data-qv-price]');
      if (variant) {
        priceEl.innerHTML = this.priceHTML(variant.price, variant.compare_at_price);
      } else {
        const first = product.variants.find((v) => v.available) || product.variants[0];
        priceEl.innerHTML = this.priceHTML(first.price, first.compare_at_price);
      }

      // image
      const img = this.qvBody.querySelector('[data-qv-img]');
      const vImg = variant?.featured_image;
      if (img && vImg) img.src = this.imgUrl(vImg, 400);

      // button
      const add = this.qvBody.querySelector('[data-qv-add]');
      const soldOut = variant && !variant.available;
      add.disabled = !!soldOut;
      add.textContent = soldOut ? this.strings.soldOut || 'Sold out' : this.strings.add || 'Add To Cart';
    }

    async quickViewAdd() {
      const { options, selected } = this.qvState;
      const missing = selected.findIndex((v) => v === null);
      const msg = this.qvBody.querySelector('[data-qv-msg]');
      if (missing > -1) {
        this.qvBody.querySelector(`[data-opt-wrap="${missing}"]`)?.classList.add('is-missing');
        msg.textContent = (this.strings.select || 'Please select a [option]').replace('[option]', options[missing].name.toLowerCase());
        return;
      }
      const variant = this.currentVariant();
      if (!variant || !variant.available) return;
      const btn = this.qvBody.querySelector('[data-qv-add]');
      const ok = await this.addToCart(variant.id, btn, (error) => {
        msg.textContent = error;
      });
      if (ok) this.closeDialog(this.qv);
    }

    /* ================= Cart ================= */
    async addToCart(variantId, button, onError) {
      if (button?.classList.contains('is-loading')) return false;
      const cart = document.querySelector('cart-drawer') || document.querySelector('cart-notification');
      button?.classList.add('is-loading');

      const body = new FormData();
      body.append('id', variantId);
      body.append('quantity', '1');
      if (cart) {
        body.append('sections', cart.getSectionsToRender().map((s) => s.id));
        body.append('sections_url', window.location.pathname);
        cart.setActiveElement?.(this.qvTrigger || button);
      }

      const fail = (message) => {
        if (onError) onError(message);
        else if (button?.closest('.cx-pc')) this.flash(button.closest('.cx-pc'), message, true);
      };

      try {
        const res = await fetch(`${window.routes?.cart_add_url || '/cart/add'}.js`, {
          method: 'POST',
          headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          body,
        });
        const data = await res.json();
        if (data.status) {
          fail(data.description || data.message || this.strings.error || 'Error');
          return false;
        }
        if (!cart) {
          window.location.href = window.routes?.cart_url || '/cart';
          return true;
        }
        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cx-quick-add', productVariantId: variantId, cartData: data });
        }
        cart.classList.remove('is-empty');
        // let the quick view slide away before the cart opens
        setTimeout(() => cart.renderContents(data), this.qv?.open ? 220 : 0);
        return true;
      } catch (err) {
        console.error('cx-quick-add', err);
        fail(this.strings.error || 'Could not add to cart');
        return false;
      } finally {
        button?.classList.remove('is-loading');
      }
    }

    flash(card, message, isError) {
      let note = card.querySelector('.cx-pc__note');
      if (!note) {
        note = document.createElement('p');
        note.className = 'cx-pc__note';
        note.setAttribute('role', 'status');
        card.querySelector('.cx-pc__media').appendChild(note);
      }
      note.textContent = message;
      note.classList.toggle('is-error', !!isError);
      clearTimeout(note._t);
      note._t = setTimeout(() => note.remove(), 3500);
    }
  }

  customElements.define('cx-collection', CxCollection);
}